/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMemo, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import TextField from '../../../../../../components/textFields/TextField'
import { baseUrl } from '../../../../../../services/baseUrl'
import labelKey from "../../../../../localization/label.json"
import {
  Autocomplete,
  Libraries,
  useJsApiLoader
} from '@react-google-maps/api'


type Props = {
  formik: any
}
const Step3 = ({ formik }: Props) => {
  const libraries: Libraries = useMemo(() => ['places'], []);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: baseUrl.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });
  const originRef = useRef<HTMLInputElement>(null);
  const wantsCc = !!formik.values.requestCorporateCreditCard;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, ref: React.RefObject<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (ref.current && ref.current.value !== '') {
        if (ref === originRef) {
          formik.setFieldValue(`findAddress`, ref.current.value);
        }
      }
    }
  };

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
  ) => {
    const updatedLocation = event.target.value;
    if (ref === originRef) {
      formik.setFieldValue(`findAddress`, updatedLocation);
      // Typing without selecting a map suggestion clears derived fields
      formik.setFieldValue('addressLine1', '');
      formik.setFieldValue('city', '');
      formik.setFieldValue('state', '');
      formik.setFieldValue('postalCode', '');
      formik.setFieldValue('latitude', '');
      formik.setFieldValue('longitude', '');
      formik.setFieldValue('country.id', null);
      formik.setFieldValue('country.title', '');
    }
  };

  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedLatitude, setSelectedLatitude] = useState('');
  const [selectedLongitude, setSelectedLongitude] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedPostalCode, setSelectedPostalCode] = useState('');

  const originAutocompleteRef = useRef<google.maps.places.Autocomplete>();
  const handleStartPlaceChanged = () => {
    const place = originAutocompleteRef.current?.getPlace();

    if (place && place.geometry && place.geometry.location) {
      const selectedLat = place.geometry.location.lat();
      const selectedLng = place.geometry.location.lng();

      setSelectedLatitude(selectedLat.toString());
      setSelectedLongitude(selectedLng.toString());

      formik.setFieldValue('latitude', selectedLat.toString());
      formik.setFieldValue('longitude', selectedLng.toString());
      const formattedAddress = place?.formatted_address ?? '';
      formik.setFieldValue('findAddress', formattedAddress);

      if (place.address_components) {
        let city = '';
        let country = '';
        let postalCode = '';
        let state = '';
        let streetNumber = '';
        let route = '';

        place.address_components.forEach(component => {
          if (component.types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (component.types.includes('route')) {
            route = component.long_name;
          }
          if (component.types.includes('locality')) {
            city = component.long_name;
            setSelectedCity(city)
            formik.setFieldValue(`city`, city);
          }
          if (component.types.includes('sublocality_level_1') && !city) {
            city = component.long_name;
            setSelectedCity(city)
            formik.setFieldValue(`city`, city);
          }
          if (component.types.includes('country')) {
            country = component.long_name;
            setSelectedCountry(country)
            formik.setFieldValue(`country.id`, 0);
            formik.setFieldValue(`country.title`, country);
          }
          if (component.types.includes('postal_code')) {
            postalCode = component.long_name;
            setSelectedPostalCode(postalCode)
            formik.setFieldValue(`postalCode`, postalCode);
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
            setSelectedState(state)
            formik.setFieldValue(`state`, state);
          }
        });
        const address = `${streetNumber} ${route}`.trim();
        setSelectedAddress(address)
        formik.setFieldValue(`addressLine1`, address || formattedAddress);
      }
    }
  };

  return (
    <>
      <div className='pb-5 pe-3' data-kt-stepper-element='content'>
        <div className='w-100'>
          <Alert variant='primary' className='mb-4 py-3'>
            <p className='fs-7 mb-2 fw-semibold'>Please correct address using the Find Address field and select a result from the map suggestions.</p>
            <p className='fs-7 mb-0'>
              After you select an address, <b>Address Line 1</b>, <b>City</b>, <b>State</b>, <b>Country</b>, <b>Postal Code</b>, <b>Latitude</b>, and <b>Longitude</b> are filled automatically.
              You can update only <b>Address Line 2</b> after searching your address on the map.
            </p>
          </Alert>
          {wantsCc && (
            <Alert variant='warning' className='mb-4 py-3'>
              <p className='fs-7 mb-0'>
                This address will be used for your <b>Corporate Credit Card</b> for shipping. Please verify the Find Address selection carefully before continuing.
              </p>
            </Alert>
          )}

          <div className='row'>
            <div className="col-md-12 mb-4">
              <label className='fs-5 fw-bold mb-2 required'>
                {labelKey.findAddress}
              </label>
              {isLoaded && (
                <Autocomplete
                  onLoad={(autocomplete) => {
                    originAutocompleteRef.current = autocomplete;
                    autocomplete.addListener('place_changed', handleStartPlaceChanged);
                  }}
                  onPlaceChanged={() => {
                  }}>
                  <input type='text'
                    className='form-control form-control-lg form-control-solid default-input'
                    placeholder='Type and select an address from the suggestions'
                    ref={originRef}
                    onKeyDown={(event) => handleKeyDown(event, originRef)}
                    onChange={(event) => handleLocationChange(event, originRef)}
                    value={formik.values.findAddress}
                  />
                </Autocomplete>
              )}
              <div className='form-text text-muted mt-2'>
                Search and select a valid address from the dropdown. Do not type a free-form address without selecting a map result.
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-6 col-md-4 col-lg-3 mb-4">
              <TextField
                label={labelKey.addressLine1}
                type='text'
                placeholder='Address Line 1'
                {...formik.getFieldProps('addressLine1')}
                value={formik.values.addressLine1 || selectedAddress}
                disabled={true}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-3 mb-4">
              <TextField
                label={`${labelKey.addressLine2} (optional)`}
                type='text'
                placeholder='Apartment, suite, unit, etc.'
                {...formik.getFieldProps('addressLine2')}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2 mb-4">
              <TextField
                label={labelKey.city}
                type='text'
                placeholder='City'
                {...formik.getFieldProps('city')}
                value={formik.values.city || selectedCity}
                disabled={true}
              />
            </div>
            <div className='col-6 col-md-4 col-lg-2 mb-4'>
              <TextField
                label={labelKey.state}
                type='text'
                placeholder='State'
                {...formik.getFieldProps('state')}
                value={formik.values.state || selectedState}
                disabled={true}
              />
            </div>
            <div className='col-6 col-md-4 col-lg-2 mb-4'>
              <TextField
                label={labelKey.country}
                type='text'
                placeholder='Country'
                value={formik.values.country?.title || selectedCountry || ''}
                disabled={true}
              />
            </div>
            <div className='col-6 col-md-4 col-lg-2 mb-4'>
              <TextField
                label={labelKey.postalCode}
                type='text'
                placeholder='Postal Code'
                {...formik.getFieldProps('postalCode')}
                value={formik.values.postalCode || selectedPostalCode}
                disabled={true}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2 mb-4">
              <TextField
                label={labelKey.latitude}
                type='text'
                placeholder='Latitude'
                {...formik.getFieldProps('latitude')}
                value={formik.values.latitude || selectedLatitude}
                disabled={true}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2 mb-4">
              <TextField
                label={labelKey.longitude}
                type='text'
                placeholder='Longitude'
                {...formik.getFieldProps('longitude')}
                value={formik.values.longitude || selectedLongitude}
                disabled={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { Step3 }
